import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Search, Users2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import SectionAssignModal from "../components/SectionAssignModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  "January 2026", "February 2026", "March 2026", "April 2026",
  "May 2026", "June 2026", "July 2026", "August 2026",
  "September 2026", "October 2026", "November 2026", "December 2026"
];

const CURRENT_MONTH = (() => {
  const names = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const d = new Date();
  return `${names[d.getMonth()]} ${d.getFullYear()}`;
})();

const STATUS_OPTIONS = ["Pending", "In Progress", "Completed"];
const TASK_STATUS_OPTIONS = ["Not Started", "Started", "Finished"];
const PRODUCT_TYPES = ["Other", "Mock", "Magazine"];

const autoDetectProductType = (particulars = "") => {
  const p = String(particulars).toLowerCase();
  if (p.includes("mock")) return "Mock";
  if (p.includes("magazine")) return "Magazine";
  return "";
};

const AllOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [masterData, setMasterData] = useState({ exams: [], subjects: [] });
  const [selectedMonth, setSelectedMonth] = useState(CURRENT_MONTH);
  const [statusFilter, setStatusFilter] = useState("all");
  const [taskStatusFilter, setTaskStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [orderForm, setOrderForm] = useState({
    client_id: "",
    exam: "",
    subject: "",
    particulars: "",
    quantity: "",
    details: "",
    status: "Pending",
    delivered_on: "",
    rate: 0,
    realised: 0,
    assigned_to: "",
    product_type: ""
  });
  const [sectionModalOrder, setSectionModalOrder] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, clientsRes, employeesRes, masterRes] = await Promise.all([
        axios.get(`${API}/orders?month=${encodeURIComponent(selectedMonth)}`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/masterdata`)
      ]);
      setOrders(ordersRes.data);
      setClients(clientsRes.data);
      setEmployees(employeesRes.data);
      
      // Organize master data
      const exams = masterRes.data.filter(d => d.category === "exam").map(d => d.value);
      const subjects = masterRes.data.filter(d => d.category === "subject").map(d => d.value);
      setMasterData({ exams, subjects });
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderForm.client_id || !orderForm.exam || !orderForm.particulars) {
      toast.error("Please fill required fields");
      return;
    }

    try {
      const payload = {
        ...orderForm,
        month: selectedMonth,
        rate: parseFloat(orderForm.rate) || 0,
        realised: parseFloat(orderForm.realised) || 0
      };

      if (editingOrder) {
        await axios.put(`${API}/orders/${editingOrder.id}`, payload);
        toast.success("Order updated");
      } else {
        await axios.post(`${API}/orders`, payload);
        toast.success("Order created");
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save order");
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setOrderForm({
      client_id: order.client_id,
      exam: order.exam,
      subject: order.subject,
      particulars: order.particulars,
      quantity: order.quantity || "",
      details: order.details || "",
      status: order.status,
      delivered_on: order.delivered_on || "",
      rate: order.rate,
      realised: order.realised || 0,
      assigned_to: order.assigned_to || "",
      product_type: order.product_type || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm("Delete this order?")) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success("Order deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete order");
    }
  };

  const handleInlineUpdate = async (orderId, field, value) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { [field]: value });
      fetchData();
    } catch (error) {
      toast.error("Update failed");
    }
  };

  const resetForm = () => {
    setEditingOrder(null);
    setOrderForm({
      client_id: "",
      exam: "",
      subject: "",
      particulars: "",
      quantity: "",
      details: "",
      status: "Pending",
      delivered_on: "",
      rate: 0,
      realised: 0,
      assigned_to: "",
      product_type: ""
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const employeeMap = useMemo(() => {
    const m = {};
    employees.forEach((e) => { m[e.id] = e; });
    return m;
  }, [employees]);

  const isMultiSection = (order) => order?.product_type === "Mock" || order?.product_type === "Magazine";

  const renderAssigneesCell = (order) => {
    if (isMultiSection(order)) {
      const sub = order.sub_assignments || [];
      const assignedIds = Array.from(new Set(sub.map((s) => s.assigned_to).filter(Boolean)));
      const names = assignedIds.map((id) => employeeMap[id]?.name).filter(Boolean);
      const done = sub.filter((s) => s.task_status === "Finished").length;
      const total = sub.length;
      if (!total || !names.length) {
        return <span className="text-slate-400">Unassigned</span>;
      }
      const display = names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${names.length - 2}` : "");
      return (
        <div className="flex flex-col gap-0.5">
          <span className="text-slate-700">{display}</span>
          <span className="text-xs text-slate-500">{done}/{total} sections done</span>
        </div>
      );
    }
    // Single-assignee
    const name = employeeMap[order.assigned_to]?.name;
    return name ? <span className="text-slate-700">{name}</span> : <span className="text-slate-400">Unassigned</span>;
  };

  const filteredOrders = orders.filter(order => {
    const client = clients.find(c => c.id === order.client_id);
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = (
      client?.name?.toLowerCase().includes(searchLower) ||
      order.particulars?.toLowerCase().includes(searchLower) ||
      order.subject?.toLowerCase().includes(searchLower)
    );
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    const matchesTask = taskStatusFilter === "all" || (order.task_status || "Not Started") === taskStatusFilter;
    return matchesSearch && matchesStatus && matchesTask;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="all-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">Viewing:</span>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-44 bg-white" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((month) => (
                <SelectItem key={month} value={month}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500">{filteredOrders.length} orders</span>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary"
          data-testid="add-order-btn"
        >
          <Plus size={16} /> Add Order
        </button>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-10"
            data-testid="search-orders"
          />
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden bg-white">
          <span className="px-3 py-1.5 text-xs text-slate-500 border-r border-slate-200 flex items-center">Status</span>
          {[["all","All"],...STATUS_OPTIONS.map((s)=>[s,s])].map(([val,lbl]) => (
            <button
              key={val}
              onClick={() => setStatusFilter(val)}
              className={`text-xs px-3 py-1.5 transition ${statusFilter === val ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
              data-testid={`status-filter-${val.replace(/\s/g,"-").toLowerCase()}`}
            >{lbl}</button>
          ))}
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden bg-white">
          <span className="px-3 py-1.5 text-xs text-slate-500 border-r border-slate-200 flex items-center">Task</span>
          {[["all","All"],...TASK_STATUS_OPTIONS.map((s)=>[s,s])].map(([val,lbl]) => (
            <button
              key={val}
              onClick={() => setTaskStatusFilter(val)}
              className={`text-xs px-3 py-1.5 transition ${taskStatusFilter === val ? "bg-prepify-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
              data-testid={`task-filter-${val.replace(/\s/g,"-").toLowerCase()}`}
            >{lbl}</button>
          ))}
        </div>
      </div>

      {/* Orders Table */}
      <div className="stat-card p-0">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold">All Orders - {selectedMonth.split(" ")[0]}</h2>
        </div>
        
        {filteredOrders.length > 0 ? (
          <div className="data-table-wrapper border-0 rounded-none">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Client</th>
                  <th>Exam</th>
                  <th>Subject</th>
                  <th>Particulars</th>
                  <th>Type</th>
                  <th>Quantity</th>
                  <th>Revenue</th>
                  <th>Realised</th>
                  <th>Assignees</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order, idx) => {
                  const client = clients.find(c => c.id === order.client_id);
                  const multi = isMultiSection(order);
                  return (
                    <tr key={order.id} data-testid={`order-row-${order.id}`}>
                      <td className="font-mono text-gray-500">{idx + 1}</td>
                      <td className="font-medium">{client?.name || "Unknown"}</td>
                      <td>{order.exam}</td>
                      <td>{order.subject}</td>
                      <td>{order.particulars}</td>
                      <td>
                        {order.product_type ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            order.product_type === "Mock" ? "bg-prepify-100 text-prepify-700" :
                            order.product_type === "Magazine" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>
                            {order.product_type}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="font-mono">{order.quantity || "-"}</td>
                      <td className="font-mono">{formatCurrency(order.rate)}</td>
                      <td className="font-mono text-green-600">{formatCurrency(order.realised)}</td>
                      <td data-testid={`assignees-${order.id}`}>{renderAssigneesCell(order)}</td>
                      <td>
                        <select
                          value={order.status}
                          onChange={(e) => handleInlineUpdate(order.id, "status", e.target.value)}
                          className={`status-select ${
                            order.status === "Completed" ? "completed" :
                            order.status === "In Progress" ? "progress" : "pending"
                          }`}
                        >
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          {multi && (
                            <button
                              onClick={() => setSectionModalOrder(order)}
                              className="btn-ghost text-prepify-600 inline-flex items-center gap-1"
                              data-testid={`assign-sections-${order.id}`}
                              title="Assign writers to sections"
                            >
                              <Users2 size={14} /> Assign
                            </button>
                          )}
                          <button
                            onClick={() => handleEdit(order)}
                            className="btn-ghost text-blue-600"
                            data-testid={`edit-order-${order.id}`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(order.id)}
                            className="btn-danger"
                            data-testid={`delete-order-${order.id}`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No orders for {selectedMonth}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl" data-testid="order-modal">
          <DialogHeader>
            <DialogTitle>{editingOrder ? "Edit Order" : "Add New Order"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={orderForm.client_id} onValueChange={(v) => setOrderForm({...orderForm, client_id: v})}>
                  <SelectTrigger data-testid="order-form-client">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Exam *</Label>
                <Select value={orderForm.exam} onValueChange={(v) => setOrderForm({...orderForm, exam: v})}>
                  <SelectTrigger data-testid="order-form-exam">
                    <SelectValue placeholder="Select exam" />
                  </SelectTrigger>
                  <SelectContent>
                    {(masterData.exams.length > 0 ? masterData.exams : ["CLAT", "AILET", "CUET", "Other"]).map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Subject</Label>
                <Select value={orderForm.subject} onValueChange={(v) => setOrderForm({...orderForm, subject: v})}>
                  <SelectTrigger data-testid="order-form-subject">
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {(masterData.subjects.length > 0 ? masterData.subjects : ["GK", "Legal Reasoning", "Other"]).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Particulars *</Label>
                <Input
                  value={orderForm.particulars}
                  onChange={(e) => {
                    const v = e.target.value;
                    setOrderForm((prev) => ({
                      ...prev,
                      particulars: v,
                      // Auto-suggest product_type only if user hasn't chosen one
                      product_type: prev.product_type || autoDetectProductType(v),
                    }));
                  }}
                  placeholder="e.g., Advanced Level Mock"
                  data-testid="order-form-particulars"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Product Type</Label>
                <Select value={orderForm.product_type || "__none__"} onValueChange={(v) => setOrderForm({...orderForm, product_type: v === "__none__" ? "" : v})}>
                  <SelectTrigger data-testid="order-form-product-type">
                    <SelectValue placeholder="Other" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Other (single assignee)</SelectItem>
                    {PRODUCT_TYPES.filter((p) => p !== "Other").map((p) => (
                      <SelectItem key={p} value={p}>{p} (multi-section)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  value={orderForm.quantity}
                  onChange={(e) => setOrderForm({...orderForm, quantity: e.target.value})}
                  placeholder="e.g., 50 Questions"
                />
              </div>
              <div className="space-y-2">
                <Label>Revenue (₹)</Label>
                <Input
                  type="number"
                  value={orderForm.rate}
                  onChange={(e) => setOrderForm({...orderForm, rate: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Realised (₹)</Label>
                <Input
                  type="number"
                  value={orderForm.realised}
                  onChange={(e) => setOrderForm({...orderForm, realised: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={orderForm.status} onValueChange={(v) => setOrderForm({...orderForm, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Delivery Date</Label>
                <Input
                  type="date"
                  value={orderForm.delivered_on}
                  onChange={(e) => setOrderForm({...orderForm, delivered_on: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Assign To</Label>
                {(orderForm.product_type === "Mock" || orderForm.product_type === "Magazine") ? (
                  <div className="text-xs text-slate-500 bg-prepify-50 border border-prepify-100 rounded-lg px-3 py-2">
                    Multi-section product. {editingOrder ? "Use the Assign button on the row to set writers per section." : "Save the order, then click Assign on the row to set writers per section."}
                  </div>
                ) : (
                  <Select value={orderForm.assigned_to} onValueChange={(v) => setOrderForm({...orderForm, assigned_to: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-order-btn">
                {editingOrder ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Section assign modal */}
      <SectionAssignModal
        open={!!sectionModalOrder}
        order={sectionModalOrder}
        employees={employees}
        onClose={() => setSectionModalOrder(null)}
        onSaved={() => fetchData()}
      />
    </div>
  );
};

export default AllOrders;
